import type { AxiosError, AxiosResponse } from 'axios';
import axios from 'axios';
import type { ZodSchema } from 'zod';

import { ApiError } from './error';
import type {
  ApiEndpointMethod,
  ApiErrorResponse,
  DefaultParamsType,
  RequestParamsBase,
  RequestParamsMakeRequest,
} from './types';

import { config } from '../../constants';
import { logoutUser } from '../auth';
import { CookieManager } from '../cookie-manager';
import { reportApiError } from '../sentry-utils';
import snackbar from '../snackbar';
import { paramsToString } from '../utility';

export class ApiClient {
  private baseUrl = config.baseURL;

  constructor(baseUrl = config.baseURL) {
    this.baseUrl = baseUrl;
  }

  private async baseRequest({
    endpoint,
    init,
    method = 'GET',
    body = undefined,
    params = {},
    isAuth = true,
    preventAuditLog = false,
    onUploadProgress,
  }: RequestParamsBase): Promise<AxiosResponse> {
    let searchParams;
    if (typeof params === 'string' && params !== '') {
      searchParams = new URLSearchParams(params);
    } else if (!!params && typeof params === 'object') {
      searchParams = new URLSearchParams(paramsToString(params));
    }

    let nBody;
    if (method !== 'GET') {
      if (body instanceof FormData) {
        nBody = body;
      } else {
        nBody = JSON.stringify(body);
      }
    }

    const accessToken = CookieManager.getAccessToken();
    const authHeaders =
      isAuth && accessToken
        ? {
            Authorization: `Bearer ${accessToken}`,
          }
        : {};

    return axios({
      ...init,
      baseURL: this.baseUrl,
      url: endpoint,
      params: searchParams,
      method,
      data: nBody,
      withCredentials: isAuth,
      onUploadProgress: onUploadProgress
        ? (progressEvent) => {
            const rawProgress = progressEvent.total ? progressEvent.loaded / progressEvent.total : 0;
            const progress = Math.round(rawProgress * 100);
            onUploadProgress(progress, rawProgress);
          }
        : undefined,
      headers: {
        ...(init?.headers || {}),
        ...authHeaders,
        /*
         * Prevent audit logs to be registered for the request
         */
        ...(preventAuditLog ? { 'X-Log-Audit': 'false' } : {}),
      },
    });
  }

  private validateResponse(response: unknown, schema?: ZodSchema): void {
    if (!schema || !response) return;
    const validationResult = schema.safeParse(
      typeof response === 'object' && 'data' in response ? response.data : response
    );
    if (validationResult && !validationResult.success) {
      // console.error(validationResult.error);
      // snackbar.error(validationResult.error.toString());
    }
  }

  private displayError(error: AxiosError<ApiErrorResponse>): void {
    const errorData = error.response?.data;

    // Report API errors to Sentry
    reportApiError(error, {
      endpoint: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      statusCode: error.response?.status,
    });

    if (errorData) {
      console.error(errorData);
      if (errorData.error) {
        const isLoggedIn = CookieManager.isLoggedIn();
        if (!(!isLoggedIn && errorData.error === 'Unauthenticated.')) {
          snackbar.error(errorData.error, {
            toastId: 'error-msg',
          });
        }
      } else if (errorData.message) {
        snackbar.error(errorData.message, {
          toastId: 'error-msg',
        });
      }
    } else {
      console.error('Something went wrong while loading data.');
      snackbar.error('Something went wrong while loading data.', {
        toastId: 'unknown-error',
      });
    }
  }

  private parseApiError(errorResponse?: ApiErrorResponse): ApiError {
    if (!errorResponse) {
      return new ApiError('Something went wrong while loading data.', null);
    }
    let errorMessage = 'Something went wrong while loading data.';

    if (errorResponse.error) {
      errorMessage = errorResponse.error as string;
    } else if (errorResponse.details) {
      const [key] = Object.keys(errorResponse.details);
      const [value] = errorResponse.details[key];
      errorMessage = value;
    } else if (errorResponse.message) {
      errorMessage = errorResponse.message;
    }

    return new ApiError(errorMessage, errorResponse);
  }

  private async makeRequest<T, P extends DefaultParamsType = DefaultParamsType>({
    responseSchema,
    endpoint,
    isAuth = true,
    ...params
  }: RequestParamsMakeRequest<P>): Promise<T> {
    try {
      // Base request call
      const res = await this.baseRequest({ endpoint, isAuth, ...params });

      if (res.status === 204) {
        // Empty response
        return res as T;
      }
      const response = res.data as T;
      this.validateResponse(response, responseSchema);
      return response;
    } catch (e: unknown) {
      const errorResponse = e as AxiosError<ApiErrorResponse>;

      if (errorResponse.response && errorResponse.response.status >= 400) {
        // Handle unauthorized response
        if (isAuth && errorResponse.response.status === 401) {
          await logoutUser();
          window.location.href = '/';
          return null as T;
        } else {
          this.displayError(errorResponse);
        }
      } else {
        this.displayError(errorResponse);
      }
      throw this.parseApiError(errorResponse.response?.data);
    }
  }

  async get<T, P extends DefaultParamsType = DefaultParamsType>({
    endpoint,
    params,
    init = {},
    ...rest
  }: Omit<RequestParamsMakeRequest<P>, 'body' | 'method'>): Promise<T> {
    return this.makeRequest<T>({
      endpoint,
      params,
      init,
      ...rest,
    });
  }

  async request<T, P = unknown>({
    endpoint,
    payload,
    init = {},
    method = 'POST',
    responseSchema,
    ...rest
  }: Omit<RequestParamsBase, 'body' | 'method'> & {
    payload?: P;
    method?: ApiEndpointMethod;
    responseSchema?: ZodSchema;
  }): Promise<T> {
    return this.makeRequest<T>({
      endpoint,
      method,
      body: payload,
      responseSchema,
      init: {
        ...init,
        headers: {
          ...(init.headers || { 'Content-Type': 'application/json' }),
        },
      },
      ...rest,
    });
  }
}

export default new ApiClient();
